import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ContactBand } from "../components/ContactBand";
import { DeanBand } from "../components/DeanBand";
import { PageHero, PageLayout } from "../components/PageShell";
import { ROUTES } from "../lib/routes";
import { SCHOLARSHIP_COUNT } from "../lib/scholarships";
import { docUrl } from "../lib/docUrl";
import { ADMISSION_CONTACTS } from "../lib/admissionContacts";
import { DEANS, deanPhoto } from "../lib/deans";
import {
  BE_FEES,
  BE_QUOTAS,
  BE_TOTAL_INTAKE,
  LATERAL_COMPONENTS,
  LATERAL_ROUTES,
  LATERAL_TOTAL,
  MBA_FEE_NOTE,
  MCA_FEES,
  MCA_TOTAL_INTAKE,
  MTECH_FEES,
  MTECH_QUOTAS,
  MTECH_TOTAL,
  MTECH_TOTAL_INTAKE,
  inr,
} from "../lib/fees";
import type { FeeComponent } from "../lib/fees";

/** An itemised fee, for the sheets that break one out rather than give a total. */
function FeeBreakdown({
  components,
  total,
  label = "Total per annum",
}: {
  components: FeeComponent[];
  total: number;
  label?: string;
}) {
  return (
    <div className="fee-breakdown">
      <ul>
        {components.map((c) => (
          <li key={c.label}>
            <span>{c.label}</span>
            <b>{inr(c.amount)}</b>
          </li>
        ))}
      </ul>
      <div className="fee-breakdown-total">
        <span>{label}</span>
        <b>{inr(total)}</b>
      </div>
    </div>
  );
}

/**
 * The three calls to action, defined once and rendered in both the hero and the
 * closing card so the two cannot drift apart.
 *
 * The prospectus used to be a dead `href="#"`. It is now the institute's own
 * Prospectus 2025-26, pulled off the old site and re-published through
 * scripts/ingest-docs.py, so all three go somewhere real.
 */
const PROSPECTUS = { url: "sites/biet/docs/admissions/prospectus-2025-26.pdf" };

const ADMISSION_ACTIONS = [
  {
    label: "Enquire",
    href: "https://docs.google.com/forms/d/e/1FAIpQLSdo16NrHcNMSPoT1TeYpubRMNlvMD9KzH87jVTJ-4jIa-LOnw/viewform",
    primary: true,
    arrow: true,
  },
  { label: "Brochure", href: docUrl(PROSPECTUS.url) },
  { label: "Campus tour", href: "https://www.youtube.com/watch?v=-wPxdLMubzY" },
];

function AdmissionActions() {
  return (
    <>
      {ADMISSION_ACTIONS.map((a) => (
        <a
          key={a.label}
          className={"btn " + (a.primary ? "btn-primary" : "btn-ghost")}
          href={a.href}
          target="_blank"
          rel="noreferrer noopener"
        >
          {a.label}
          {a.arrow && <span> →</span>}
        </a>
      ))}
    </>
  );
}

const ADM_DEAN = DEANS.find((d) => d.role === "Dean Admissions");
/** Her published contact, so the band can carry a phone and an address. */
const ADM_DEAN_CONTACT = ADMISSION_CONTACTS.find((c) => c.role.includes("Dean"));

export default function Admissions() {
  // Scroll-spy: highlight the program / resource section currently in view.
  const [activeId, setActiveId] = useState("be");
  useEffect(() => {
    const ids = ["be", "mtech", "mca", "mba", "phd", "contact"];
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <PageHero
        variant="hero-navy hero-compact"
        fullWidthTitle
        eyebrow="Admissions · AY 2026 — 27"
        title="Apply to BIET."
        lead="Admissions are open across B.E., M.Tech, MBA, MCA and Ph.D. programs. Review eligibility, send an enquiry, and take the campus tour."
        crumbs={[{ label: "Admissions" }]}
        image={null}
        actions={<AdmissionActions />}
      />
      <PageLayout
        side={[
          { title: "Apply to", items: [
            { label: "BE", to: "#be", active: activeId === "be" },
            { label: "M.Tech", to: "#mtech", active: activeId === "mtech" },
            { label: "MCA", to: "#mca", active: activeId === "mca" },
            { label: "MBA", to: "#mba", active: activeId === "mba" },
            { label: "Ph.D.", to: "#phd", active: activeId === "phd" },
          ]},
          { title: "Resources", items: [
            { label: "Who to contact", to: "#contact", active: activeId === "contact" },
            { label: "Scholarships", to: ROUTES.scholarships },
            { label: "Hostel", to: ROUTES.hostels },
            { label: "Mandatory Disclosures", to: ROUTES.disclosures },
          ]},
        ]}
      >
        <h2 id="be">How to apply — B.E. (Undergraduate)</h2>
        <p>Admission to undergraduate engineering programs is governed by the Karnataka Examinations Authority (KEA) and COMEDK. BIET also offers a management quota for direct admission. Below is the step-by-step process.</p>
        <div className="admit-facts">
          <div className="admit-fact">
            <div className="admit-fact-label">Eligibility</div>
            <div className="admit-fact-value">Class 12 with Physics, Mathematics and one of Chemistry / Biology / Computer Science — minimum 45% (40% for reserved categories). Entrance via KCET / COMEDK.</div>
          </div>
          <div className="admit-fact">
            <div className="admit-fact-label">Annual tuition</div>
            <div className="admit-fact-value">
              ₹1,50,320 a year under the CET quota, the same for every branch. COMEDK and
              management fees vary by programme — the notified figures are below.
            </div>
          </div>
        </div>
        <div className="process" style={{ marginTop: "var(--space-6)" }}>
          <div className="process-step"><h4>Check eligibility</h4><p>Class 12 with Physics, Mathematics, and one of Chemistry/Biology/Computer Science with at least 45% (40% for reserved categories).</p></div>
          <div className="process-step"><h4>Appear for KCET / COMEDK</h4><p>Register and write the Karnataka CET (KEA) or COMEDK UGET. Management quota does not require an entrance exam.</p></div>
          <div className="process-step"><h4>Counselling / direct apply</h4><p>Through KEA seat allotment for KCET; COMEDK counselling; or apply directly via the BIET admissions portal for management quota.</p></div>
          <div className="process-step"><h4>Document verification</h4><p>Report to BIET admissions office with originals — marksheets, transfer certificate, conduct certificate, ID proof and recent photographs.</p></div>
        </div>

        <h3>Fee by programme</h3>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Programme</th>
                <th scope="col" className="num">Intake</th>
                {BE_QUOTAS.map((q) => (
                  <th key={q.id} scope="col" className="num">
                    {q.label}
                    {q.share && <span className="data-table-sub">{q.share}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BE_FEES.map((r) => (
                <tr key={r.course}>
                  <th scope="row">{r.course}</th>
                  <td className="num">{r.intake}</td>
                  {BE_QUOTAS.map((q) => (
                    <td key={q.id} className="num">{inr(r.fees[q.id])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total intake</th>
                <td className="num">{BE_TOTAL_INTAKE}</td>
                <td className="num" colSpan={BE_QUOTAS.length} />
              </tr>
            </tfoot>
          </table>
        </div>

        <h3>Lateral entry — diploma quota</h3>
        <p>
          Diploma holders join in the third semester. Every branch carries the same fee,
          across both routes the notification names: {LATERAL_ROUTES.join(", and ")}.
        </p>
        <FeeBreakdown components={LATERAL_COMPONENTS} total={LATERAL_TOTAL} />

        <h2 id="mtech">How to apply — M.Tech</h2>
        <p>M.Tech admissions are through Karnataka PGCET and valid GATE scores, with a limited management quota. Specializations are offered within the respective engineering departments.</p>
        <div className="admit-facts">
          <div className="admit-fact">
            <div className="admit-fact-label">Eligibility</div>
            <div className="admit-fact-value">B.E. / B.Tech in a relevant discipline — minimum 50% (45% for reserved). Entrance via Karnataka PGCET or a valid GATE score.</div>
          </div>
          <div className="admit-fact">
            <div className="admit-fact-label">Annual tuition</div>
            <div className="admit-fact-value">
              {inr(MTECH_TOTAL)} a year, the same across every specialization and both quotas.
            </div>
          </div>
        </div>
        <div className="process" style={{ marginTop: "var(--space-6)" }}>
          <div className="process-step"><h4>Check eligibility</h4><p>B.E./B.Tech in a relevant discipline with at least 50% (45% for reserved categories).</p></div>
          <div className="process-step"><h4>Appear for PGCET / GATE</h4><p>Register for the Karnataka PGCET (M.Tech) or hold a valid GATE score. GATE-qualified candidates may be eligible for a stipend.</p></div>
          <div className="process-step"><h4>Counselling / direct apply</h4><p>Through KEA seat allotment for PGCET / GATE, sponsored and part-time routes, or the management quota via the BIET portal.</p></div>
          <div className="process-step"><h4>Document verification</h4><p>Report with originals — degree marksheets, PGCET/GATE scorecard, transfer certificate, ID proof and photographs.</p></div>
        </div>

        <h3>Fee by programme</h3>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Programme</th>
                <th scope="col" className="num">Intake</th>
                {MTECH_QUOTAS.map((q) => (
                  <th key={q.id} scope="col" className="num">
                    {q.label}
                    {q.share && <span className="data-table-sub">{q.share}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MTECH_FEES.map((r) => (
                <tr key={r.course}>
                  <th scope="row">{r.course}</th>
                  <td className="num">{r.intake}</td>
                  {/* The sheet prices both quotas the same, on every programme. */}
                  {MTECH_QUOTAS.map((q) => (
                    <td key={q.id} className="num">{inr(MTECH_TOTAL)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total intake</th>
                <td className="num">{MTECH_TOTAL_INTAKE}</td>
                <td className="num" colSpan={MTECH_QUOTAS.length} />
              </tr>
            </tfoot>
          </table>
        </div>

        <h2 id="mca">How to apply — MCA</h2>
        <p>MCA admissions are through Karnataka PGCET, with a management quota. A strong quantitative background is expected.</p>
        <div className="admit-facts">
          <div className="admit-fact">
            <div className="admit-fact-label">Eligibility</div>
            <div className="admit-fact-value">Bachelor's degree with Mathematics (10+2 or degree level) — minimum 50% (45% for reserved). Entrance via Karnataka PGCET.</div>
          </div>
          <div className="admit-fact">
            <div className="admit-fact-label">Annual tuition</div>
            <div className="admit-fact-value admit-fact-fees">
              {MCA_FEES.map((f) => (
                <span key={f.quota}>
                  <b>{inr(f.amount)}</b>
                  <em>{f.quota}</em>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="process" style={{ marginTop: "var(--space-6)" }}>
          <div className="process-step"><h4>Check eligibility</h4><p>Bachelor's degree with Mathematics (at 10+2 or degree level) and at least 50% (45% for reserved categories).</p></div>
          <div className="process-step"><h4>Appear for PGCET</h4><p>Register and write the Karnataka PGCET (MCA).</p></div>
          <div className="process-step"><h4>Counselling / direct apply</h4><p>Through KEA seat allotment, or apply directly via the BIET admissions portal for management quota.</p></div>
          <div className="process-step"><h4>Document verification</h4><p>Report with originals — degree marksheets, PGCET scorecard, transfer certificate, ID proof and photographs.</p></div>
        </div>
        <p className="fee-seatline">
          Intake is {MCA_TOTAL_INTAKE} in total —{" "}
          {MCA_FEES.map((f) => `${f.intake} on the ${f.quota.toLowerCase()}`).join(", and ")}.
        </p>

        <h2 id="mba">How to apply — MBA</h2>
        <p>The MBA program is AICTE-approved and VTU-affiliated, admitting through Karnataka PGCET and KMAT, with a management quota.</p>
        <div className="admit-facts">
          <div className="admit-fact">
            <div className="admit-fact-label">Eligibility</div>
            <div className="admit-fact-value">Any recognized bachelor's degree — minimum 50% (45% for reserved). Entrance via Karnataka PGCET or KMAT.</div>
          </div>
          <div className="admit-fact">
            <div className="admit-fact-label">Annual tuition</div>
            <div className="admit-fact-value">{MBA_FEE_NOTE}</div>
          </div>
        </div>
        <div className="process" style={{ marginTop: "var(--space-6)" }}>
          <div className="process-step"><h4>Check eligibility</h4><p>Any recognized bachelor's degree with at least 50% (45% for reserved categories).</p></div>
          <div className="process-step"><h4>Appear for PGCET / KMAT</h4><p>Write the Karnataka PGCET or KMAT. Valid CAT/MAT scores are also considered where applicable.</p></div>
          <div className="process-step"><h4>Counselling / direct apply</h4><p>Through KEA seat allotment, or apply directly via the BIET admissions portal for management quota.</p></div>
          <div className="process-step"><h4>Document verification</h4><p>Report with originals — degree marksheets, entrance scorecard, transfer certificate, ID proof and photographs.</p></div>
        </div>

        <h2 id="phd">How to apply — Ph.D.</h2>
        <p>BIET hosts fifteen VTU-recognised research centres. Doctoral admission is through the VTU research entrance and an interview before the Doctoral Committee.</p>
        <div className="admit-facts">
          <div className="admit-fact">
            <div className="admit-fact-label">Eligibility</div>
            <div className="admit-fact-value">Master's degree in a relevant discipline — minimum 55% (50% for reserved). Entrance via the VTU research test (NET / GATE / SLET exemption applies).</div>
          </div>
          <div className="admit-fact">
            <div className="admit-fact-label">Annual tuition</div>
            <div className="admit-fact-value">
              Research fees are set by VTU and the institute does not publish them on its
              fee notification; confirm with the research centre.
            </div>
          </div>
        </div>
        <div className="process" style={{ marginTop: "var(--space-6)" }}>
          <div className="process-step"><h4>Check eligibility</h4><p>Master's degree in a relevant discipline with at least 55% (50% for reserved categories).</p></div>
          <div className="process-step"><h4>Research entrance</h4><p>Qualify the VTU Ph.D. entrance test, or claim exemption with a valid UGC-NET / GATE / SLET qualification.</p></div>
          <div className="process-step"><h4>Proposal &amp; interview</h4><p>Submit a research proposal and present it before the Doctoral Committee at the relevant research centre.</p></div>
          <div className="process-step"><h4>Registration</h4><p>Provisional registration with an approved research supervisor, followed by first-year coursework.</p></div>
        </div>

        {/* The full list now has its own page — fees belong here, but which
            outside office sanctions which scheme does not. Left as a pointer so
            an applicant reading about fees still finds it. */}
        <h2 id="scholarships-pointer">Scholarships</h2>
        <p>
          {SCHOLARSHIP_COUNT} government, corporate-CSR and private-trust scholarships are
          open to BIET students — apply directly through each provider.
        </p>
        <Link className="btn btn-ghost" to={ROUTES.scholarships}>
          See all scholarships <span>→</span>
        </Link>

        <div style={{ marginTop: "var(--space-7)", padding: "var(--space-6)", background: "var(--bg-alt)", border: "1px solid var(--rule)" }}>
          <div className="kicker">Ready when you are</div>
          <h3 style={{ marginTop: 12, marginBottom: 14 }}>Start your application</h3>
          <p style={{ marginBottom: 22 }}>Online application opens 1 May 2026. Counselling support is available Mon — Sat, 9:30 AM — 5:30 PM.</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <AdmissionActions />
          </div>
        </div>

      </PageLayout>

      {/* The office, before its desks — same shape as /placements and
          /examinations. Her contact details ride on the band, so she is not
          repeated in the rows below; that duplication is what the other two
          pages had to have removed. */}
      {ADM_DEAN && (
        <DeanBand
          kicker="Office of Admissions"
          title="One office takes every application in."
          name={ADM_DEAN.name}
          role={ADM_DEAN.role}
          photo={deanPhoto(ADM_DEAN)}
          phone={ADM_DEAN_CONTACT?.phone}
          email={ADM_DEAN_CONTACT?.email}
        >
          Admissions run across B.E., M.Tech, MBA, MCA and Ph.D., through KEA, COMEDK,
          PGCET and the management quota. Eligibility, counselling, document
          verification and the campus visit are all handled by this office.
        </DeanBand>
      )}

      {/* Keeps the #contact id the side rail links to. The Dean is absent here
          deliberately — she is named once, on the band above. */}
      <ContactBand
        id="contact"
        title="The admissions office."
        entries={ADMISSION_CONTACTS.filter((c) => c.name !== ADM_DEAN_CONTACT?.name).map((c) => ({
          role: c.role,
          name: c.name,
          phone: c.phone,
          email: c.email,
        }))}
      />

    </>
  );
}
